const BYTE_UNITS: string[] = ['B', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

const BIBYTE_UNITS: string[] = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

const BIT_UNITS = ['b', 'kbit', 'Mbit', 'Gbit', 'Tbit', 'Pbit', 'Ebit', 'Zbit', 'Ybit'];

const BIBIT_UNITS: string[] = ['b', 'kibit', 'Mibit', 'Gibit', 'Tibit', 'Pibit', 'Eibit', 'Zibit', 'Yibit'];

/*
Formats the given number using `Number#toLocaleString`.
- If locale is a string, the value is expected to be a locale-key (for example: `de`).
- If locale is true, the system default locale is used for translation.
- If no value for locale is specified, the number is returned unmodified.
*/
function toLocaleString(number: number | bigint, locale?: Intl.LocalesArgument | boolean, options?: (Intl.NumberFormatOptions & BigIntToLocaleStringOptions) | undefined): string {
  let resultString: string | null = null;
  if (typeof locale === 'string' || Array.isArray(locale)) {
    resultString = number.toLocaleString(locale, options);
  } else if (locale === true || options !== undefined) {
    resultString = number.toLocaleString(undefined, options);
  }
  return resultString || number.toString();
}

function log10(numberOrBigInt: number | bigint) {
  if (typeof numberOrBigInt === 'number') {
    return Math.log10(numberOrBigInt);
  }

  const string: string = numberOrBigInt.toString(10);

  return string.length + Math.log10(Number('0.' + string.slice(0, 15)));
}

function log(numberOrBigInt: number | bigint) {
  if (typeof numberOrBigInt === 'number') {
    return Math.log(numberOrBigInt);
  }

  return log10(numberOrBigInt) * Math.log(10);
}

function divide(numberOrBigInt: number | bigint, divisor: number): number {
  if (typeof numberOrBigInt === 'number') {
    return numberOrBigInt / divisor;
  }

  const integerPart: bigint = numberOrBigInt / BigInt(divisor);
  const remainder: bigint = numberOrBigInt % BigInt(divisor);
  return Number(integerPart) + Number(remainder) / divisor;
}

export const prettyBytes: PrettyBytesFn = function (number: number | bigint, options?: Options): string {
  if (typeof number !== 'bigint' && !Number.isFinite(number)) {
    throw new TypeError(`Expected a finite number, got ${typeof number}: ${number}`);
  }

  options = {
    bits: false,
    binary: false,
    space: true,
    ...options,
  };

  const UNITS: string[] = options.bits ? (options.binary ? BIBIT_UNITS : BIT_UNITS) : options.binary ? BIBYTE_UNITS : BYTE_UNITS;

  const separator: string = options.space ? ' ' : '';

  if (options.signed && (typeof number === 'number' ? number === 0 : number === 0n)) {
    return `0${separator}${UNITS[0]}`;
  }

  const isNegative: boolean = number < 0;
  const prefix: string = isNegative ? '-' : options.signed ? '+' : '';

  if (isNegative) {
    number = -number;
  }

  let localeOptions: (Intl.NumberFormatOptions & BigIntToLocaleStringOptions) | undefined = undefined;

  if (options.minimumFractionDigits !== undefined) {
    localeOptions = { minimumFractionDigits: options.minimumFractionDigits };
  }

  if (options.maximumFractionDigits !== undefined) {
    localeOptions = {
      maximumFractionDigits: options.maximumFractionDigits,
      ...localeOptions,
    };
  }

  if (number < 1) {
    const numberString: string = toLocaleString(number, options.locale, localeOptions);
    return prefix + numberString + separator + UNITS[0];
  }

  const exponent: number = Math.min(Math.floor(options.binary ? log(number) / Math.log(1024) : log10(number) / 3), UNITS.length - 1);
  number = divide(number, (options.binary ? 1024 : 1000) ** exponent);

  if (!localeOptions) {
    const minPrecision: number = Math.max(3, Number.parseInt(number.toString(), 10).toString().length);
    number = Number(number.toPrecision(minPrecision));
  }

  const numberString: string = toLocaleString(number, options.locale, localeOptions);

  const unit = UNITS[exponent];

  return prefix + numberString + separator + unit;
};

export type Options = {
  /**
	Include plus sign for positive numbers. If the difference is exactly zero a space character will be prepended instead for better alignment.

	@default false
	*/
  readonly signed?: boolean;

  /**
	- If `false`: Output won't be localized.
	- If `true`: Localize the output using the system/browser locale.
	- If `string`: Expects a [BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) (For example: `en`, `de`, …)
	- If `string[]`: Expects a list of [BCP 47 language tags](https://en.wikipedia.org/wiki/IETF_language_tag) (For example: `en`, `de`, …)

	@default false
	*/
  readonly locale?: boolean | string | readonly string[];

  /**
	Format the number as [bits](https://en.wikipedia.org/wiki/Bit) instead of [bytes](https://en.wikipedia.org/wiki/Byte). This can be useful when, for example, referring to [bit rate](https://en.wikipedia.org/wiki/Bit_rate).

	@default false

	@example
	```

prettyBytes(1337, { bits: true });
//=> '1.34 kbit'
	*/
  readonly bits?: boolean;

  /**
	Format the number using the [Binary Prefix](https://en.wikipedia.org/wiki/Binary_prefix) instead of the [SI Prefix](https://en.wikipedia.org/wiki/SI_prefix). This can be useful for presenting memory amounts. However, this should not be used for presenting file sizes.

	@default false

	@example
	```
	import prettyBytes from 'pretty-bytes';

	prettyBytes(1000, {binary: true});
	//=> '1000 bit'

	prettyBytes(1024, {binary: true});
	//=> '1 kiB'
	```
	*/
  readonly binary?: boolean;

  /**
	The minimum number of fraction digits to display.

	If neither `minimumFractionDigits` or `maximumFractionDigits` are set, the default behavior is to round to 3 significant digits.

	@default undefined

	@example
	```
	import prettyBytes from 'pretty-bytes';

	// Show the number with at least 3 fractional digits
	prettyBytes(1900, {minimumFractionDigits: 3});
	//=> '1.900 kB'

	prettyBytes(1900);
	//=> '1.9 kB'
	```
	*/
  readonly minimumFractionDigits?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20;

  /**
	The maximum number of fraction digits to display.

	If neither `minimumFractionDigits` or `maximumFractionDigits` are set, the default behavior is to round to 3 significant digits.

	@default undefined

	@example
	```
	import prettyBytes from 'pretty-bytes';

	// Show the number with at most 1 fractional digit
	prettyBytes(1920, {maximumFractionDigits: 1});
	//=> '1.9 kB'

	prettyBytes(1920);
	//=> '1.92 kB'
	```
	*/
  readonly maximumFractionDigits?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20;

  /**
	Put a space between the number and unit.

	@default true

	@example
	```
	import prettyBytes from 'pretty-bytes';

	prettyBytes(1920, {space: false});
	//=> '1.9kB'

	prettyBytes(1920);
	//=> '1.92 kB'
	```
	*/
  readonly space?: boolean;
};

/**
Convert bytes to a human readable string: `1337` → `1.34 kB`.

@param number - The number to format.

@example
```
import prettyBytes from 'pretty-bytes';

prettyBytes(1337);
//=> '1.34 kB'

prettyBytes(100);
//=> '100 B'

// Display file size differences
prettyBytes(42, {signed: true});
//=> '+42 B'

// Localized output using German locale
prettyBytes(1337, {locale: 'de'});
//=> '1,34 kB'
```
*/
export interface PrettyBytesFn {
  (number: number | bigint, options?: Options): string;
}
